// calculators/average-daily-balance/src/calculator.ts
// UI logic for the public Average Daily Balance Calculator.
//
// The average daily balance method adds up the balance for every day of the
// billing cycle, divides by the number of days, then charges simple interest on
// that average:  interest = ADB × (APR/365) × cycleDays.
//
// Self-contained math: NO import from shared/payoffSimulation (that module
// daily-COMPOUNDS, which is the wrong model for the ADB method). No chart in v1
// either, so there is no import from _shared/multiSeriesChart.

const CALC_ID = "average_daily_balance";

// ── Math types (exported for testing) ────────────────────────────────────────

type TxnType = "charge" | "payment";

export interface AdbTransaction {
  dayOfCycle: number; // 1..cycleDays. Balance changes AT THE START of this day.
  type: TxnType; // "charge" → adds to balance; "payment" → subtracts.
  amount: number; // positive magnitude (>0). Sign derived from `type`.
}

export interface AdbInput {
  startingBalance: number; // balance on day 1 before any listed txn (>= 0)
  annualRatePct: number; // APR as a percent, e.g. 22 (not 0.22)
  cycleDays: number; // 28..31, default 30
  transactions: AdbTransaction[]; // 0..N. Empty = flat balance for the cycle.
}

export interface AdbResult {
  averageDailyBalance: number; // Σ(daily balances)/cycleDays
  interestCharged: number; // ADB × (APR/365) × cycleDays
  effectiveDailyRate: number; // APR/365 as a DECIMAL (e.g. 0.22/365)
  dailyBalances: number[]; // length === cycleDays, post-clamp balance per day
}

// ── Exported math functions ──────────────────────────────────────────────────

function normalizeCycleDays(cycleDays: number): number {
  const d = Math.trunc(cycleDays);
  if (!Number.isFinite(d) || d < 28 || d > 31) return 30;
  return d;
}

function safeAmount(amount: number): number {
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return amount;
}

/**
 * Build the per-day balance array, applying each txn at the START of its
 * dayOfCycle, clamping at 0 (a payment cannot drive the balance negative).
 */
export function buildDailyBalances(input: AdbInput): number[] {
  const cycleDays = normalizeCycleDays(input.cycleDays);
  let balance = Math.max(0, Number.isFinite(input.startingBalance) ? input.startingBalance : 0);

  const txns = Array.isArray(input.transactions) ? input.transactions : [];

  const daily: number[] = [];
  for (let d = 1; d <= cycleDays; d++) {
    // Apply every txn whose dayOfCycle === d, in input order, before recording.
    for (const txn of txns) {
      if (txn.dayOfCycle !== d) continue;
      if (txn.dayOfCycle < 1 || txn.dayOfCycle > cycleDays) continue; // out of cycle
      const amt = safeAmount(txn.amount);
      if (txn.type === "charge") {
        balance += amt;
      } else {
        balance = Math.max(0, balance - amt);
      }
    }
    daily.push(balance);
  }
  return daily;
}

/** Σ(daily balances)/cycleDays. */
export function computeAdb(input: AdbInput): number {
  const daily = buildDailyBalances(input);
  if (daily.length === 0) return 0;
  const sum = daily.reduce((s, b) => s + b, 0);
  return sum / daily.length;
}

/** Full result. interest = ADB × (annualRatePct/100/365) × cycleDays. */
export function computeAdbInterest(input: AdbInput): AdbResult {
  const cycleDays = normalizeCycleDays(input.cycleDays);
  const dailyBalances = buildDailyBalances(input);
  const averageDailyBalance = computeAdb(input);

  const apr = Number.isFinite(input.annualRatePct) ? Math.max(0, input.annualRatePct) : 0;
  const effectiveDailyRate = apr / 100 / 365;
  const interestCharged = averageDailyBalance * effectiveDailyRate * cycleDays;

  return {
    averageDailyBalance,
    interestCharged,
    effectiveDailyRate,
    dailyBalances,
  };
}

// ── Small helpers ────────────────────────────────────────────────────────────

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function t(
  key: string,
  fallback: string,
  params?: Record<string, string | number>
): string {
  const w = window as unknown as {
    i18next?: { t: (k: string, p?: Record<string, string | number>) => string };
  };
  if (w.i18next && typeof w.i18next.t === "function") {
    const val = w.i18next.t(key, { defaultValue: fallback, ...(params || {}) });
    return typeof val === "string" ? val : fallback;
  }
  if (params) {
    return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      params[k] === undefined ? `{{${k}}}` : String(params[k])
    );
  }
  return fallback;
}

function gtag(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Current site locale — same source/priority as js/i18n.js and store-tracking.js
// (i18next active language → <html lang> → ?lang= → 'en'). Sent on every event.
function getLang(): string {
  const SUPPORTED = ["en", "es", "fr", "en-GB", "en-AU", "en-CA", "fr-CA"];
  try {
    const w = window as unknown as {
      i18next?: { language?: string };
      getSiteLang?: () => string;
    };
    if (typeof w.getSiteLang === "function") return w.getSiteLang();
    if (w.i18next && typeof w.i18next.language === "string" && w.i18next.language) {
      return w.i18next.language;
    }
    const docLang = document.documentElement.getAttribute("lang");
    if (docLang && SUPPORTED.indexOf(docLang) !== -1) return docLang;
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && SUPPORTED.indexOf(param) !== -1) return param;
  } catch {
    /* fall through */
  }
  return "en";
}

function formatCurrency(n: number): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s,$]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parsePctRaw(raw: string): number | null {
  const cleaned = raw.replace(/[\s,%]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// ── Validation / parsing of the live form ────────────────────────────────────

const MAX_AMOUNT = 100000;
const MAX_APR_PCT = 50;
const MIN_DAYS = 28;
const MAX_DAYS = 31;

type FieldName = "balance" | "apr" | "days";

interface ParsedInputs {
  startingBalance: number;
  annualRatePct: number;
  cycleDays: number;
  transactions: AdbTransaction[];
}

interface ValidationResult {
  valid: boolean;
  values?: ParsedInputs;
  errors: Partial<Record<FieldName, string>>;
  txnError: string | null;
}

interface RawTxnRow {
  id: number;
  day: string;
  type: string;
  amount: string;
}

let txnRows: RawTxnRow[] = [];
let nextTxnId = 1;

function readScalarInputs(): Record<FieldName, string> {
  return {
    balance: ($("adb-input-balance") as HTMLInputElement | null)?.value || "",
    apr: ($("adb-input-apr") as HTMLInputElement | null)?.value || "",
    days: ($("adb-input-days") as HTMLInputElement | null)?.value || "",
  };
}

function readTxnRows(): RawTxnRow[] {
  return txnRows.map((row) => ({
    id: row.id,
    day: ($(`adb-txn-day-${row.id}`) as HTMLInputElement | null)?.value || "",
    type: ($(`adb-txn-type-${row.id}`) as HTMLSelectElement | null)?.value || "charge",
    amount: ($(`adb-txn-amount-${row.id}`) as HTMLInputElement | null)?.value || "",
  }));
}

function validate(
  raw: Record<FieldName, string>,
  rawTxns: RawTxnRow[]
): ValidationResult {
  const errors: Partial<Record<FieldName, string>> = {};

  const balance = parseAmount(raw.balance);
  if (balance === null || balance < 0) {
    errors.balance = t(
      "calculators.adb.error_balance_required",
      "Enter a starting balance of 0 or more."
    );
  } else if (balance > MAX_AMOUNT) {
    errors.balance = t(
      "calculators.adb.error_balance_too_large",
      "Balance must be $100,000 or less."
    );
  }

  const apr = parsePctRaw(raw.apr);
  if (apr === null || apr < 0) {
    errors.apr = t("calculators.adb.error_apr_required", "Enter an APR of 0 or more.");
  } else if (apr > MAX_APR_PCT) {
    errors.apr = t("calculators.adb.error_apr_too_large", "APR must be 50% or less.");
  }

  const daysParsed = raw.days.trim() === "" ? 30 : Number(raw.days.replace(/[\s,]/g, ""));
  let cycleDays = 30;
  if (!Number.isFinite(daysParsed) || daysParsed < MIN_DAYS || daysParsed > MAX_DAYS) {
    errors.days = t(
      "calculators.adb.error_days_invalid",
      "Billing cycle must be between 28 and 31 days."
    );
  } else {
    cycleDays = Math.trunc(daysParsed);
  }

  // Transactions: each row must be a valid day within the cycle and a non-negative
  // amount. Blank rows (no day AND no amount) are ignored.
  const transactions: AdbTransaction[] = [];
  let txnError: string | null = null;
  for (const row of rawTxns) {
    const dayBlank = row.day.trim() === "";
    const amtBlank = row.amount.trim() === "";
    if (dayBlank && amtBlank) continue; // ignore an empty row

    const day = Number(row.day.replace(/[\s,]/g, ""));
    const amount = parseAmount(row.amount);
    if (
      !Number.isFinite(day) ||
      day < 1 ||
      (cycleDays && day > cycleDays) ||
      Math.trunc(day) !== day ||
      amount === null ||
      amount <= 0 ||
      amount > MAX_AMOUNT
    ) {
      txnError = t(
        "calculators.adb.error_txn_invalid",
        "Each transaction needs a day within the cycle and a positive amount."
      );
      continue;
    }
    transactions.push({
      dayOfCycle: Math.trunc(day),
      type: row.type === "payment" ? "payment" : "charge",
      amount,
    });
  }

  if (Object.keys(errors).length === 0 && balance !== null && apr !== null && !txnError) {
    return {
      valid: true,
      values: {
        startingBalance: balance,
        annualRatePct: apr,
        cycleDays,
        transactions,
      },
      errors,
      txnError: null,
    };
  }
  return { valid: false, errors, txnError };
}

function renderFieldError(field: FieldName, message: string | null): void {
  const errEl = $(`adb-error-${field}`);
  const inputEl = $(`adb-input-${field}`);
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "" : "none";
  }
  if (inputEl) {
    if (message) inputEl.setAttribute("aria-invalid", "true");
    else inputEl.removeAttribute("aria-invalid");
  }
}

function renderTxnError(message: string | null): void {
  const errEl = $("adb-error-txn");
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "" : "none";
  }
}

// ── Transaction add-row UI ────────────────────────────────────────────────────

function renderTxnRow(row: RawTxnRow): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "adb-txn-row";
  wrap.id = `adb-txn-row-${row.id}`;

  const dayLabel = t("calculators.adb.txn_day_label", "Day");
  const typeLabel = t("calculators.adb.txn_type_label", "Type");
  const amountLabel = t("calculators.adb.txn_amount_label", "Amount");
  const chargeLabel = t("calculators.adb.txn_type_charge", "Charge");
  const paymentLabel = t("calculators.adb.txn_type_payment", "Payment");
  const removeLabel = t("calculators.adb.txn_remove_label", "Remove transaction");

  wrap.innerHTML =
    `<div class="adb-txn-field">` +
    `<label for="adb-txn-day-${row.id}">${dayLabel}</label>` +
    `<input id="adb-txn-day-${row.id}" type="number" inputmode="numeric" step="1" min="1" max="31" placeholder="1" autocomplete="off" value="${row.day}" />` +
    `</div>` +
    `<div class="adb-txn-field">` +
    `<label for="adb-txn-type-${row.id}">${typeLabel}</label>` +
    `<select id="adb-txn-type-${row.id}">` +
    `<option value="charge"${row.type === "charge" ? " selected" : ""}>${chargeLabel}</option>` +
    `<option value="payment"${row.type === "payment" ? " selected" : ""}>${paymentLabel}</option>` +
    `</select>` +
    `</div>` +
    `<div class="adb-txn-field adb-txn-amount">` +
    `<label for="adb-txn-amount-${row.id}">${amountLabel}</label>` +
    `<div class="tc-field-wrap">` +
    `<span class="tc-field-prefix">$</span>` +
    `<input id="adb-txn-amount-${row.id}" type="number" inputmode="decimal" step="0.01" min="0" placeholder="0" autocomplete="off" class="has-prefix" value="${row.amount}" />` +
    `</div>` +
    `</div>` +
    `<button type="button" class="adb-txn-remove" data-txn-remove="${row.id}" aria-label="${removeLabel}" title="${removeLabel}">&times;</button>`;

  return wrap;
}

function addTxnRow(): void {
  const row: RawTxnRow = { id: nextTxnId++, day: "", type: "charge", amount: "" };
  txnRows.push(row);
  const list = $("adb-txn-list");
  if (!list) return;
  const el = renderTxnRow(row);
  list.appendChild(el);

  // Wire input listeners for the new row.
  for (const sub of ["day", "type", "amount"]) {
    const input = $(`adb-txn-${sub}-${row.id}`);
    if (input)
      input.addEventListener("input", () => {
        const errBox = $("adb-runtime-error");
        if (errBox) errBox.style.display = "none";
        scheduleRecalc();
      });
  }
  const removeBtn = el.querySelector<HTMLButtonElement>("[data-txn-remove]");
  if (removeBtn)
    removeBtn.addEventListener("click", () => {
      removeTxnRow(row.id);
    });
}

function removeTxnRow(id: number): void {
  txnRows = txnRows.filter((r) => r.id !== id);
  const el = $(`adb-txn-row-${id}`);
  if (el && el.parentElement) el.parentElement.removeChild(el);
  scheduleRecalc();
}

// ── Results rendering ────────────────────────────────────────────────────────

function showResults(): void {
  const r = $("adb-results");
  if (r) r.style.display = "block";
}

function hideResults(): void {
  const r = $("adb-results");
  if (r) r.style.display = "none";
}

function formatDailyRate(rate: number): string {
  // Show enough precision to be meaningful (e.g. 0.0603% per day).
  const pct = rate * 100;
  return `${pct.toFixed(4)}%`;
}

function renderResults(result: AdbResult): void {
  const heroEl = $("adb-result-hero");
  if (heroEl) {
    heroEl.innerHTML = t(
      "calculators.adb.result_hero",
      "Your average daily balance is <strong>{{adb}}</strong>",
      { adb: formatCurrency(result.averageDailyBalance) }
    );
  }

  const adbEl = $("adb-result-adb");
  if (adbEl) {
    adbEl.innerHTML =
      t("calculators.adb.metric_adb", "Average daily balance") +
      `: <strong>${formatCurrency(result.averageDailyBalance)}</strong>`;
  }

  const interestEl = $("adb-result-interest");
  if (interestEl) {
    interestEl.innerHTML =
      t("calculators.adb.metric_interest", "Interest this cycle") +
      `: <strong>${formatCurrency(result.interestCharged)}</strong>`;
  }

  const rateEl = $("adb-result-daily-rate");
  if (rateEl) {
    rateEl.innerHTML =
      t("calculators.adb.metric_daily_rate", "Effective daily rate") +
      `: <strong>${formatDailyRate(result.effectiveDailyRate)}</strong>`;
  }

  gtag("event", "calc_result_shown", {
    calculator: CALC_ID,
    placement: "calculator",
    txn_count: txnRows.length,
    lang: getLang(),
  });
}

// ── Run / debounce ───────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;
const GA_INPUT_DEBOUNCE_MS = 1000;

let recalcTimer: number | null = null;
let gaInputTimer: number | null = null;
let lastChangedField: string | null = null;

function run(): void {
  const raw = readScalarInputs();
  const rawTxns = readTxnRows();
  const v = validate(raw, rawTxns);

  (["balance", "apr", "days"] as FieldName[]).forEach((f) =>
    renderFieldError(f, v.errors[f] || null)
  );
  renderTxnError(v.txnError);

  if (!v.valid || !v.values) {
    hideResults();
    return;
  }

  try {
    const result = computeAdbInterest(v.values);
    showResults();
    renderResults(result);
  } catch (err) {
    const msg =
      err instanceof Error ? err.message.slice(0, 80) : String(err).slice(0, 80);
    hideResults();
    const errBox = $("adb-runtime-error");
    if (errBox) {
      errBox.textContent = t(
        "calculators.adb.runtime_error",
        "Something went wrong. Try refreshing the page."
      );
      errBox.style.display = "block";
    }
    gtag("event", "calc_error", { calculator: CALC_ID, placement: "calculator", message: msg, lang: getLang() });
  }
}

function scheduleRecalc(): void {
  if (recalcTimer !== null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    run();
  }, DEBOUNCE_MS);
}

function scheduleGaInputEvent(): void {
  if (gaInputTimer !== null) window.clearTimeout(gaInputTimer);
  gaInputTimer = window.setTimeout(() => {
    gaInputTimer = null;
    if (lastChangedField) {
      gtag("event", "calc_input_change", {
        calculator: CALC_ID,
        placement: "calculator",
        field: lastChangedField,
        lang: getLang(),
      });
    }
  }, GA_INPUT_DEBOUNCE_MS);
}

export function attachCalculator(): void {
  (["balance", "apr", "days"] as FieldName[]).forEach((field) => {
    const el = $(`adb-input-${field}`) as HTMLInputElement | null;
    if (!el) return;
    el.addEventListener("input", () => {
      lastChangedField = field;
      const errBox = $("adb-runtime-error");
      if (errBox) errBox.style.display = "none";
      scheduleRecalc();
      scheduleGaInputEvent();
    });
  });

  const addBtn = $("adb-txn-add") as HTMLButtonElement | null;
  if (addBtn)
    addBtn.addEventListener("click", (e) => {
      e.preventDefault();
      addTxnRow();
      lastChangedField = "txn_add";
      scheduleGaInputEvent();
    });

  document.querySelectorAll<HTMLAnchorElement>("[data-calc-cta]").forEach((a) => {
    a.addEventListener("click", () => {
      const store = a.getAttribute("data-calc-cta") || "unknown";
      const lang = getLang();
      // Keep calc_cta_click (it has GA history). The unified `app_store_click`
      // event is owned solely by /js/store-tracking.js (global delegated
      // listener) to avoid double-counting.
      gtag("event", "calc_cta_click", { calculator: CALC_ID, placement: "calculator", store, lang });
    });
  });

  const calcButton = $("adb-calculate-button") as HTMLButtonElement | null;
  if (calcButton) {
    calcButton.addEventListener("click", (e) => {
      e.preventDefault();
      run();
    });
  }

  const form = $("adb-form") as HTMLFormElement | null;
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      run();
    });
  }

  gtag("event", "calc_view", { calculator: CALC_ID, placement: "calculator", lang: getLang() });

  // Auto-run once a balance is entered (APR + days have defaults), so we don't
  // paint a validation error on load.
  if (readScalarInputs().balance) run();
}
